import { type ModelContext, useAui } from '@assistant-ui/react';
import React, { use, useEffect } from 'react';
import { useStore } from 'zustand';

import { AtlasInstructions } from '#plugin-chat/components/agent/AtlasInstructions.tsx';
import { Tool } from '#plugin-chat/components/tools/Tool.ts';
import { InstructionStoreContext } from '#plugin-chat/contexts/InstructionStoreContext.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { selectInstructions } from '#plugin-chat/lib/instructions/instructionStore.ts';

const toolByType = new Map(Object.entries(Tool).map(([key, tool]) => [tool, key]));

interface AtlasAssistantProps {
  children?: React.ReactNode;
  name?: string;
  description?: string;
  modelContext?: Partial<ModelContext>;
}

export function AtlasAssistant({ children, name = 'Cognite Atlas AI™', modelContext }: AtlasAssistantProps) {
  const aui = useAui();
  // Registered `system`-placement instruction blocks, joined in priority order.
  // Subscribes to the store and re-renders only when the joined text changes.
  const system = useStore(use(InstructionStoreContext), selectInstructions('system'));
  console.debug('Atlas Assistant instructions:', system);

  // Only register model context when this agent becomes active
  useEffect(
    () => aui.modelContext().register({ getModelContext: () => ({ system, ...modelContext }) }),
    [aui, system, modelContext],
  );

  const toolsMap = new Map<string, string>(
    React.Children.toArray(children)
      .filter(
        (child): child is React.ReactElement<{ name: string }> =>
          React.isValidElement(child) &&
          typeof child.props === 'object' &&
          !!child.props &&
          'name' in child.props &&
          !!child.props?.name,
      )
      .map((child) => [
        toolByType.get(child.type as (typeof Tool)[keyof typeof Tool]) ?? '<missing-tool>',
        child.props.name,
      ]),
  );

  const tools = new Proxy(
    {},
    {
      get(_target, prop, _receiver) {
        if (!toolsMap.has(prop as string)) {
          console.error(`Invalid tool reference: ${prop.toString()}, valid: ${[...toolsMap.keys()].join(', ')}`);
          return `<invalid-tool:${prop.toString()}>`;
        }
        // Plain wire name — flow signals (forceTool/narrowTools) and the
        // citation scans match tool-call parts' toolName against these
        // values; wrap with toolRef only when embedding in prompt prose.
        return toolsMap.get(prop as string);
      },
    },
  ) as { [ToolNameType in keyof typeof Tool]: string };

  return (
    <ToolsContext value={tools}>
      <AtlasInstructions name={name} />
      {children}
    </ToolsContext>
  );
}
